import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import Text from '../../components/text';
import { Ionicons, AntDesign } from '@expo/vector-icons';
import { useRoute } from '@react-navigation/native';
import { COLORS, BORDER_RADIUS, SHADOWS, LAYOUT } from '../../constants/theme';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';

/* ─── Setup-progress sidebar steps config ─────────────────────── */
export const SETUP_STEPS = [
  { key: 'location', statusKey: 'location_details_status', routeName: 'PropertyAddress', icon: 'location-outline', label: 'Location & Address' },
  { key: 'property', statusKey: 'room_details_status', routeName: 'PropertyDetails', icon: 'home-outline', label: 'Property Details' },
  { key: 'landlord', statusKey: 'landlord_details_status', routeName: 'LandlordDetails', icon: 'person-outline', label: 'Landlord Details' },
  { key: 'certification', statusKey: 'certificate_details_status', routeName: 'Certification', icon: 'shield-checkmark-outline', label: 'Certification' },
  { key: 'requirements', statusKey: 'property_requirements_status', routeName: 'Requirements', icon: 'list-outline', label: 'Requirements' },
  { key: 'licensing', statusKey: 'license_details_status', routeName: 'Licensing', icon: 'document-text-outline', label: 'Licensing' },
  { key: 'finance', statusKey: 'finance_details_status', routeName: 'financeDetails', icon: 'card-outline', label: 'Finance' },
  { key: 'utility', statusKey: 'utilities_details_status', routeName: 'Utilities', icon: 'flash-outline', label: 'Utility' },
];

/* ─── Reusable SetupSidebar Component ───────────────────────────── */
export const SetupSidebar = ({ activeKey, propertyId, propertyName, navigation, statuses }) => {
  const [fetchedStatuses, setFetchedStatuses] = useState({});

  useEffect(() => {
    if (propertyId && !statuses) {
      ajax
        .get(Urls.Properties + '/' + propertyId)
        .then(({ data }) => {
          if (data?.status && data?.payload) {
            setFetchedStatuses(data.payload);
          }
        })
        .catch(() => {});
    }
  }, [propertyId, statuses]);

  const activeStatusMap = statuses || fetchedStatuses;

  const getItemStyles = (stepKey, statusKey) => {
    const isActive = stepKey === activeKey;
    if (isActive) {
      return {
        rowBg: '#F5EEFF',
        iconBg: COLORS.primary || '#6C2BD9',
        iconColor: '#FFFFFF',
        labelColor: COLORS.primary || '#6C2BD9',
        fontWeight: '700',
        badgeIcon: 'chevron-forward',
        badgeColor: COLORS.primary || '#6C2BD9',
      };
    }

    const st = activeStatusMap?.[statusKey];
    if (st === 2) {
      // Completed - Green
      return {
        rowBg: '#ECFDF5',
        iconBg: '#DCFCE7',
        iconColor: '#16A34A',
        labelColor: '#16A34A',
        fontWeight: '600',
        badgeIcon: 'checkmark-circle',
        badgeColor: '#16A34A',
      };
    } else if (st === 1) {
      // In Progress - Yellow
      return {
        rowBg: '#FFFBEB',
        iconBg: '#FEF3C7',
        iconColor: '#D97706',
        labelColor: '#D97706',
        fontWeight: '600',
        badgeIcon: 'time',
        badgeColor: '#D97706',
      };
    }

    // Not Started - Grey (st === 0 or undefined)
    return {
      rowBg: '#FFFFFF',
      iconBg: '#F3F4F6',
      iconColor: '#9CA3AF',
      labelColor: '#6B7280',
      fontWeight: '500',
      badgeIcon: null,
      badgeColor: '#9CA3AF',
    };
  };

  return (
    <View style={sb.sidebar}>
      <View style={sb.card}>
        <Text style={sb.title}>Setup progress</Text>
        <Text style={sb.sub}>Complete initial setup to continue.</Text>
        <View style={sb.list}>
          {SETUP_STEPS.map((step) => {
            const styleProps = getItemStyles(step.key, step.statusKey);
            return (
              <TouchableOpacity
                key={step.key}
                style={[sb.row, { backgroundColor: styleProps.rowBg }]}
                activeOpacity={0.7}
                onPress={() => {
                  if (step.routeName) {
                    const navParams = {};
                    if (propertyId) navParams.id = propertyId;
                    if (propertyName) navParams.name = propertyName;
                    navigation.navigate(step.routeName, navParams);
                  }
                }}
              >
                <View style={[sb.icon, { backgroundColor: styleProps.iconBg }]}>
                  <Ionicons
                    name={step.icon}
                    size={15}
                    color={styleProps.iconColor}
                  />
                </View>
                <Text
                  style={[
                    sb.label,
                    { color: styleProps.labelColor, fontWeight: styleProps.fontWeight },
                  ]}
                  numberOfLines={1}
                >
                  {step.label}
                </Text>
                {styleProps.badgeIcon ? (
                  <Ionicons
                    name={styleProps.badgeIcon}
                    size={14}
                    color={styleProps.badgeColor}
                  />
                ) : null}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </View>
  );
};

const sb = StyleSheet.create({
  sidebar: { width: 240, flexShrink: 0 },
  card: { backgroundColor: '#fff', borderRadius: 14, padding: 18, ...SHADOWS.md },
  title: { fontSize: 15, fontWeight: '700', color: '#111827', marginBottom: 4 },
  sub: { fontSize: 12, color: '#9CA3AF', marginBottom: 16, lineHeight: 17 },
  list: { gap: 4 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, paddingHorizontal: 10, borderRadius: 10 },
  rowActive: { backgroundColor: '#F5EEFF' },
  icon: { width: 30, height: 30, borderRadius: 8, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  iconActive: { backgroundColor: COLORS.primary || '#6C2BD9' },
  label: { fontSize: 13, color: '#6B7280', fontWeight: '500', flex: 1 },
  labelActive: { color: COLORS.primary || '#6C2BD9', fontWeight: '700' },
});

/* ─── Custom Hook for Persistent Route Parameters ──────────────── */
export const usePropertyRouteParams = () => {
  const route = useRoute();
  const routeParams = route?.params || {};

  // Try extracting from window location search if on web
  let searchParams = {};
  if (typeof window !== 'undefined' && window.location && window.location.search) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('id')) searchParams.id = urlParams.get('id');
      if (urlParams.get('name')) searchParams.name = urlParams.get('name');
      if (urlParams.get('key')) searchParams.key = urlParams.get('key');
      if (urlParams.get('status')) searchParams.status = urlParams.get('status');
    } catch (e) {
      // ignore URLSearchParams parse errors
    }
  }

  // SessionStorage fallback for web refresh
  let sessionParams = {};
  if (typeof sessionStorage !== 'undefined') {
    try {
      const cachedId = sessionStorage.getItem('active_property_id');
      const cachedName = sessionStorage.getItem('active_property_name');
      if (cachedId) sessionParams.id = cachedId;
      if (cachedName) sessionParams.name = cachedName;
    } catch (e) {
      // ignore sessionStorage errors
    }
  }

  // Try extracting from window location pathname if path params like /PropertyInfo/461
  let pathId = '';
  if (typeof window !== 'undefined' && window.location && window.location.pathname) {
    try {
      const match = window.location.pathname.match(/\/(?:PropertyInfo|PropertyDetails|PropertyDetailsReadOnly|propertySection|financeDetails|Certification|Licensing|Requirements|Utilities)\/([^\/?#]+)/i);
      if (match && match[1]) {
        pathId = match[1];
      }
    } catch (e) {}
  }

  const resolvedId = routeParams.id || searchParams.id || pathId || sessionParams.id || '';
  const resolvedName = routeParams.name || searchParams.name || sessionParams.name || 'Property';
  const resolvedKey = routeParams.key || searchParams.key || '';
  const resolvedStatus = routeParams.status !== undefined ? routeParams.status : (searchParams.status !== undefined ? searchParams.status : 0);

  // Persist resolved property ID and Name to sessionStorage for seamless refresh
  if (typeof sessionStorage !== 'undefined' && resolvedId) {
    try {
      sessionStorage.setItem('active_property_id', String(resolvedId));
      if (resolvedName) sessionStorage.setItem('active_property_name', String(resolvedName));
    } catch (e) {
      // ignore sessionStorage errors
    }
  }

  return {
    id: resolvedId,
    name: resolvedName,
    key: resolvedKey,
    status: Number(resolvedStatus),
    rawParams: routeParams,
  };
};

/* ─── Reusable Property Header Banner Card ────────────────────── */
export const PropertyHeaderBanner = ({ icon = 'home-outline', title, subtitle, rightComponent, isMobile }) => (
  <View style={[SHARED_BANNER_STYLES.bannerCard, isMobile && SHARED_BANNER_STYLES.bannerCardMobile]}>
    <View style={SHARED_BANNER_STYLES.bannerLeft}>
      <View style={SHARED_BANNER_STYLES.bannerIconWrap}>
        <Ionicons name={icon} size={22} color="#6C2BD9" />
      </View>
      <View style={SHARED_BANNER_STYLES.bannerTextWrap}>
        <Text style={SHARED_BANNER_STYLES.bannerTitle}>{title}</Text>
        {subtitle ? <Text style={SHARED_BANNER_STYLES.bannerSubtitle}>{subtitle}</Text> : null}
      </View>
    </View>
    {rightComponent ? <View style={SHARED_BANNER_STYLES.bannerRight}>{rightComponent}</View> : null}
  </View>
);

/* ─── Reusable Property Breadcrumbs Bar ───────────────────────── */
export const PropertyBreadcrumb = ({ navigation, propertyId, propertyName = 'Property', currentStep }) => (
  <View style={bc.bar}>
    {/* <TouchableOpacity
      onPress={() => navigation.navigate('PropertySection', { id: propertyId, name: propertyName })}
      style={bc.backBtn}
    >
      <AntDesign name="left" size={12} color="#6C2BD9" />
      <Text style={bc.backText}>Back</Text>
    </TouchableOpacity> */}
    <Text style={bc.sep}>Properties</Text>
    <Text style={bc.sep}>›</Text>
    <Text style={bc.activeText}>{propertyName}</Text>
    {currentStep ? (
      <>
        <Text style={bc.sep}>›</Text>
        <Text style={bc.currentText}>{currentStep}</Text>
      </>
    ) : null}
  </View>
);

const bc = StyleSheet.create({
  bar: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: 20 },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  sep: { fontSize: 13, color: '#9CA3AF' },
  activeText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  currentText: { fontSize: 13, color: '#111827', fontWeight: '600' },
});

/* ─── Reusable Property Sticky Action Footer ──────────────────── */
export const PropertyStickyFooter = ({ onSave, buttonText = 'Save and continue →', noteText = 'All fields marked * are required', isMobile }) => (
  <View style={[ft.stickyFooter, { bottom: 0, justifyContent: isMobile ? 'flex-end' : 'space-between' }]}>
    {!isMobile && (
      <Text style={ft.footerNote} numberOfLines={1}>{noteText}</Text>
    )}
    <TouchableOpacity style={ft.footerBtn} onPress={onSave} activeOpacity={0.85}>
      <Text style={ft.footerBtnText}>{buttonText}</Text>
    </TouchableOpacity>
  </View>
);

const ft = StyleSheet.create({
  stickyFooter: {
    position: 'absolute',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
    zIndex: 1000,
    ...SHADOWS.md,
  },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 24, paddingVertical: 12, },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

/* ─── Shared Banner Card Styles ───────────────────────────────── */
export const SHARED_BANNER_STYLES = StyleSheet.create({
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    margin: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},
});
